/**
 * Egzersiz ödevinin madde madde detayı.
 *
 * "%60 doğru" bir şey anlatmıyor. Uygulayıcının sorduğu soru şu: hangi
 * maddede ne yazdı? Boş mu bıraktı, yanlış mı yazdı, yoksa fazladan mı
 * işaretledi? Üçü üç ayrı bulgu:
 *
 *   — çok sayıda BOŞ madde genelde süre ya da vazgeçme sorunu,
 *   — yanlış ama tutarlı cevaplar kuralı yanlış anlamış olmak,
 *   — fazladan işaret ise "hepsini işaretleyip geçeyim" davranışı.
 *
 * Oran üçünü de aynı gösteriyor. Bu yüzden maddeler ayrı ayrı duruyor
 * ve boş bırakılanlarla fazladan işaretlenenler ayrı sayılıyor.
 */

import { useCallback, useEffect, useState } from 'react'
import { egzersizAdi, api, odevAdresi, type OdevDetayi as Detay } from '../api'
import { Baglanti } from '../Baglanti'

const ALAN_ADI: Record<string, string> = {
  sustained_attention: 'Dikkati sürdürme',
  selective_attention: 'Seçici dikkat',
  working_memory_visual: 'Görsel işleyen bellek',
  working_memory_verbal: 'Sözel işleyen bellek',
  processing_speed: 'İşlem hızı',
  inhibition: 'Dürtü kontrolü',
  cognitive_flexibility: 'Bilişsel esneklik',
}

function sure(ms: number | null): string {
  if (ms === null) return '—'
  const sn = Math.round(ms / 1000)
  return sn < 60 ? `${sn} sn` : `${Math.floor(sn / 60)} dk ${sn % 60} sn`
}

function gun(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' })
}

export function OdevDetayi({
  odevId,
  yazabilir,
  onGeri,
}: {
  odevId: string
  yazabilir: boolean
  onGeri: () => void
}) {
  const [detay, setDetay] = useState<Detay | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const [baglanti, setBaglanti] = useState<{ adres: string; saat: number } | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      setDetay(await api.odev(odevId))
    } catch {
      setHata('Ödev açılamadı.')
    }
  }, [odevId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  if (hata) {
    return (
      <>
        <div className="uyari">{hata}</div>
        <button onClick={onGeri}>← Geri</button>
      </>
    )
  }
  if (!detay) return <p className="alt">Yükleniyor…</p>

  const { odev, sonuc } = detay
  const maddeler = sonuc?.maddeler ?? []
  const bos = maddeler.filter((m) => m.verilen === null && !m.fazladan).length
  const yanlis = maddeler.filter((m) => m.verilen !== null && !m.dogru && !m.fazladan).length
  const fazla = maddeler.filter((m) => m.fazladan).length
  const isaretGorevi = maddeler.some((m) => m.beklenen === 'işaretlenmeli')

  const baglantiUret = async () => {
    setBekliyor(true)
    try {
      const r = await api.odevBaglantisi(odevId)
      setBaglanti({ adres: odevAdresi(r.jeton), saat: Math.round(r.gecerlilikSn / 3600) })
    } catch {
      setHata('Bağlantı üretilemedi. Kâğıt ödevlerin ekranda oynanacak hali yok.')
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onGeri}>← Dosya</button>
        {sonuc && <button onClick={() => window.print()}>Yazdır</button>}
      </div>

      <h1>{egzersizAdi(odev.kod)}</h1>
      <p className="alt">
        {ALAN_ADI[odev.alan] ?? odev.alan} · {odev.hafta}. hafta · düzey {odev.duzey} ·{' '}
        {odev.kanal === 'digital' ? 'tablet' : 'kâğıt'}
      </p>

      {odev.yonerge && (
        <div className="kart">
          <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Çocuğa verilen yönerge</strong>
          <p style={{ margin: '6px 0 0' }}>{odev.yonerge}</p>
        </div>
      )}

      {!sonuc ? (
        <>
          <div className="bilgi">Bu ödev henüz yapılmadı.</div>
          {yazabilir && odev.kanal !== 'print' && (
            <div className="kart">
              <h2 style={{ margin: '0 0 4px' }}>Çocuğun cihazında aç</h2>
              <p className="alt" style={{ marginBottom: 12 }}>
                Tek kullanımlık bir bağlantı üretilir. Çocuk hesap açmaz; bağlantı yalnızca bu
                ödeve geçerlidir ve yapılınca kapanır.
              </p>
              <div className="dugmeler">
                <button className="birincil" disabled={bekliyor} onClick={() => void baglantiUret()}>
                  {bekliyor ? 'Üretiliyor…' : 'Bağlantı üret'}
                </button>
              </div>
              {baglanti && (
                <Baglanti
                  adres={baglanti.adres}
                  kare
                  mesaj="Bu haftanın çalışması. Tabletten açıp sessiz bir ortamda yapabilir."
                  not={`Bağlantı ${baglanti.saat} saat geçerli ve çalışma bitince kapanır.`}
                />
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="alan">
            <div className="alan-ad">
              {sonuc.dogru} / {sonuc.toplam} doğru (%{Math.round(Number(sonuc.oran) * 100)})
            </div>
            <div className="alan-olcut">
              <span>
                Süre: <strong style={{ color: 'var(--ink)' }}>{sure(sonuc.sureMs)}</strong>
              </span>
              <span>
                Yapıldı: <strong style={{ color: 'var(--ink)' }}>{gun(sonuc.yapildi)}</strong>
              </span>
              {bos > 0 && (
                <span>
                  Boş bırakılan: <strong style={{ color: 'var(--ink)' }}>{bos}</strong>
                </span>
              )}
              {yanlis > 0 && (
                <span>
                  Yanlış: <strong style={{ color: 'var(--ink)' }}>{yanlis}</strong>
                </span>
              )}
              {fazla > 0 && (
                <span>
                  Fazladan işaret: <strong style={{ color: 'var(--ink)' }}>{fazla}</strong>
                </span>
              )}
            </div>
            {bos > 0 && bos >= maddeler.length / 2 && (
              <div className="alan-kaynak">
                Maddelerin yarısından fazlası boş. Bu genelde doğruluk sorunu değil, süre ya da
                vazgeçme sorunudur; seansta sormaya değer.
              </div>
            )}
            {fazla > 0 && fazla >= 3 && (
              <div className="alan-kaynak">
                Fazladan işaret sayısı yüksek. Doğruları bulmaktan çok "hepsini işaretleyip
                geçme" örüntüsü olabilir.
              </div>
            )}
          </div>

          <h2>Madde madde</h2>
          {maddeler.length === 0 ? (
            <div className="bilgi">
              Bu ödevde madde detayı kaydedilmemiş. Detay kaydı eklenmeden önce yapılmış olabilir.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{isaretGorevi ? 'Hücre' : 'Madde'}</th>
                  <th>Doğrusu</th>
                  <th>Verdiği</th>
                  <th>Sonuç</th>
                </tr>
              </thead>
              <tbody>
                {maddeler.map((m, i) => (
                  <tr key={`${m.id}-${i}`}>
                    <td>{m.fazladan ? `${m.id} (fazladan)` : m.id}</td>
                    <td>{m.beklenen}</td>
                    <td style={{ color: m.verilen === null ? 'var(--muted)' : 'var(--ink)' }}>
                      {m.verilen ?? 'boş'}
                    </td>
                    <td>
                      <span className={'rozet' + (m.dogru ? ' ok' : m.fazladan ? ' warn' : ' bad')}>
                        {m.dogru ? 'Doğru' : m.fazladan ? 'Fazladan' : m.verilen === null ? 'Boş' : 'Yanlış'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  )
}
